import React, { useCallback, useState, useRef, useEffect, Fragment, useMemo, RefObject, ReactElement } from 'react';
import clsx from 'clsx';
import { get } from 'lodash';
import { useClickOutside, useEventListener } from '../../hooks';
import { SearchSelectItem } from './SearchSelect';
import Item from './SearchItem';
import { Spinner } from '../Spinner';

const LoadingSpinnerListItem = () => (
  <li className='text-black-default cursor-pointer font-effra p-3 border-transparent'>
    <div className='flex items-center justify-center'>
      <Spinner />
    </div>
  </li>
);

const scrollConfig: ScrollIntoViewOptions = {
  block: 'nearest',
  behavior: 'smooth'
};

interface ItemsListProps {
  options: Array<SearchSelectItem>;
  selectedItem?: SearchSelectItem;
  onItemSelected: (item: SearchSelectItem) => void;
  expanded: boolean;
  label: string;
  maxVisible: number;
  searchInput: RefObject<HTMLInputElement>;
  handleClose: () => void;
  setExpanded: (isExpanded: boolean) => void;
  dataIsLoading: boolean;
  buildTemplate: (item: SearchSelectItem) => ReactElement;
  closeOnSelect?: boolean;
  noSearchResultsMessage?: string;
}
const SearchItemsList = ({
  options,
  selectedItem,
  onItemSelected,
  expanded = false,
  label,
  buildTemplate,
  maxVisible = 4,
  searchInput,
  handleClose,
  setExpanded,
  dataIsLoading,
  noSearchResultsMessage = 'No items available...',
  closeOnSelect
}: ItemsListProps) => {
  const listRef = useRef<HTMLUListElement>(null);
  const [hovered, setHovered] = useState(-1);

  useEffect(() => {
    setHovered(-1);
  }, [expanded]);

  const itemHeight = get(searchInput, 'current?.offsetHeight', 48);

  const optionsLength = useMemo(
    () => options.reduce((acc, option) => (option?.items ? acc + option.items.length + 1 : acc + 1), 0),
    [options]
  );

  const handleKeyDown = useCallback(
    (event) => {
      const { keyCode } = event;
      let next;
      switch (keyCode) {
        case 40: // down
          next = hovered + 1;
          if (next < options.length) {
            setHovered(next);
            listRef.current!.children[next].scrollIntoView(scrollConfig);
            if (!expanded) {
              setExpanded(true);
            }
          }
          break;
        case 38: // up
          next = hovered - 1;
          if (next >= 0) {
            listRef.current!.children[next].scrollIntoView(scrollConfig);
            setHovered(next);
          }
          break;
        case 27: // esc
          if (expanded) {
            event.stopPropagation();
          }
          setExpanded(false);
          break;
        case 13: // enter
          event.preventDefault(); // don't submit the form by accident
          if (options[hovered]) {
            onItemSelected(options[hovered]);
            setExpanded(false);
          }
          break;
        case 9: // tab
          handleClose();
          setHovered(-1);
          break;
        case 35: // end
          event.preventDefault();
          next = options.length - 1;
          setHovered(next);
          listRef.current!.children[next].scrollIntoView(scrollConfig);
          break;
        case 36: // home
          event.preventDefault();
          setHovered(0);
          listRef.current!.children[0].scrollIntoView(scrollConfig);
          break;
        default:
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hovered, options, expanded, listRef, onItemSelected]
  );

  const handleClickOutside = useCallback(
    (e) => {
      if (expanded) {
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expanded]
  );

  useEventListener('keydown', handleKeyDown, searchInput.current);
  useClickOutside(listRef, handleClickOutside);

  const optionsListStyle = useCallback(() => {
    const optionsQty = optionsLength > maxVisible ? maxVisible : optionsLength;

    const optionItemHeight = get(listRef, 'current.firstChild.offsetHeight', itemHeight);
    const openToTop =
      (searchInput?.current?.getBoundingClientRect().bottom || 0) + optionsQty * optionItemHeight >
      document.body.clientHeight;

    const css = {
      transform: `translateY(-${optionsQty * itemHeight + itemHeight + 2}px)`,
      borderRadius: '0.5rem 0.5rem 0 0',
      marginTop: '0.5rem'
    };

    return openToTop ? css : { transform: 'translateY(-1rem)' };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length, maxVisible, itemHeight, searchInput]);

  const handleSelect = useCallback(
    (chosenItem) => {
      closeOnSelect && setExpanded(false);
      onItemSelected(chosenItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setExpanded, onItemSelected]
  );

  const itemsList = (itemsArray: SearchSelectItem[]) => {
    return itemsArray.map((item: SearchSelectItem, idx) => {
      if (item.items) {
        const subItemsList = itemsList(item.items);
        return (
          <Fragment key={`${item.value}-${item.label}`}>
            <div className='text-gray-55 p-3 bg-gray-5 border-b-2 box-border border-gray-default'>{item.label}</div>
            {subItemsList}
          </Fragment>
        );
      }
      return (
        <Item
          key={`${item.value}-${item.label}`}
          item={item}
          buildTemplate={buildTemplate}
          onItemSelected={handleSelect}
          isSelected={selectedItem === item}
          isHovered={hovered === idx}
        />
      );
    });
  };

  return (
    <div
      className={clsx(
        'w-full pointer-events-none rounded-lg rounded-t-none z-30 mt-3 overflow-hidden shadow-menu absolute',
        !expanded && 'invisible'
      )}
      style={optionsListStyle()}
      data-testid='searchItems-list'
    >
      <ul
        ref={listRef}
        tabIndex={-1}
        className='mb-0 overflow-auto bg-white border-t-0 pointer-events-auto'
        style={{ maxHeight: `${maxVisible * itemHeight}px` }}
        role='listbox'
        id={`listbox-${label}`}
        aria-label={label}
      >
        {dataIsLoading ? (
          <LoadingSpinnerListItem />
        ) : options.length ? (
          itemsList(options)
        ) : (
          <li className='p-3'>{noSearchResultsMessage}</li>
        )}
      </ul>
    </div>
  );
};

export default SearchItemsList;
